import type { PrinterConnectMode } from './BluetoothPrinterService';
import { getPrinterCapabilities } from './platform';

const LS_KEY = 'zeettu.xpP203a.bluetoothTransport';

function envTruthy(raw: string | undefined): boolean {
  const v = String(raw ?? '').trim().toLowerCase();
  return v === '1' || v === 'true' || v === 'yes';
}

/** When set, default XP-P203A Bluetooth transport is BLE GATT (no localStorage override). */
export function xpP203aForceBleGatt(): boolean {
  return envTruthy(import.meta.env?.VITE_PRINTER_P203A_FORCE_BLE_GATT);
}

/** Web Bluetooth GATT vs Bluetooth Classic RFCOMM over Web Serial. */
export type XpP203aBluetoothTransport = 'ble' | 'bluetooth-serial';

function parseStoredTransport(raw: string | null | undefined): XpP203aBluetoothTransport | null {
  const v = String(raw ?? '').trim().toLowerCase();
  if (v === 'ble' || v === 'gatt') return 'ble';
  if (v === 'bluetooth-serial' || v === 'serial' || v === 'rfcomm' || v === 'spp') {
    return 'bluetooth-serial';
  }
  return null;
}

/** Saved per browser; falls back to env force-BLE, then RFCOMM when Web Serial SPP is available. */
export function getXpP203aBluetoothTransport(): XpP203aBluetoothTransport {
  if (typeof globalThis !== 'undefined' && 'localStorage' in globalThis) {
    try {
      const stored = parseStoredTransport(globalThis.localStorage.getItem(LS_KEY));
      if (stored) return stored;
    } catch {
      /* ignore */
    }
  }
  if (xpP203aForceBleGatt()) return 'ble';
  if (getPrinterCapabilities().webSerial) return 'bluetooth-serial';
  return 'ble';
}

export function setXpP203aBluetoothTransport(transport: XpP203aBluetoothTransport): void {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return;
  try {
    globalThis.localStorage.setItem(LS_KEY, transport);
  } catch {
    /* quota */
  }
}

export function clearXpP203aBluetoothTransport(): void {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) return;
  try {
    globalThis.localStorage.removeItem(LS_KEY);
  } catch {
    /* ignore */
  }
}

export function xpP203aConnectModeFromTransport(
  transport: XpP203aBluetoothTransport,
): Extract<PrinterConnectMode, 'ble' | 'bluetooth-serial'> {
  return transport;
}
